
/**
 * VWA device list for VWA users: every VWA of a current project shown as license plate.
 * The plate is taken from the section description, e.g. "VWA HD-IM-2023 (12)".
 * A click opens the information modal with only the settings tab to switch the pictures.
 */
import { Vue, Component } from "vue-property-decorator";
import { mapMutations } from "vuex";

import SingleBoardInformationModal from "@/components/project/SingleProject/Schematic/InformationModal/SingleBoardInformationModal.vue";
import { ProjectTypeInterfaceInformationModal } from "@/model/MainProject/MainProjectSection";
import VwaDevices from "@/services/vwaService";
import { matchesLicensePlate } from "@/utils/LicensePlateSearch";

interface VwaDevice {
  sectionId: number;
  projectId: number;
  projectName: string;
  description: string;
  licensePlate: string | null;
  boardUid: string;
  online: boolean;
  locked: boolean;
}

const VWA_PROJECT_TYPE: ProjectTypeInterfaceInformationModal = {
  isRZP: false,
  isFPU: false,
  isVSU: false,
  isSCENE: false,
  isCWU: false,
  isVWA: true,
  isMq3Aq32SW: false,
  isMq4Aq42SWDEGES: false,
  isMq3Aq32023: false,
  isMq4Aq42SW: false,
  isMq5Aq52SW: false,
  isCwuCascaded: false,
  isMq1Aq12SW: false
};

/** district, letters and number of a German plate, e.g. "HD-IB-738" -> HD | IB 738 */
const GERMAN_PLATE = /^([A-ZÄÖÜ]{1,3})[-\s]+([A-Z]{1,2})[-\s]+(\d{1,4}[EH]?)$/i;

/** five pointed star around (0|0) with radius 1, scaled into the EU circle */
const STAR_POINTS = Array.from({ length: 10 }, (_, i) => {
  const radius = i % 2 === 0 ? 1 : 0.4;
  const angle = -Math.PI / 2 + (i * Math.PI) / 5;
  return `${(radius * Math.cos(angle)).toFixed(3)},${(radius * Math.sin(angle)).toFixed(3)}`;
}).join(" ");

/** the twelve stars of the EU flag, arranged in a circle */
const EU_STARS = Array.from({ length: 12 }, (_, i) => {
  const angle = (i * Math.PI) / 6;
  return `translate(${(20 + 13 * Math.cos(angle)).toFixed(2)} ${(20 + 13 * Math.sin(angle)).toFixed(2)}) scale(3.4)`;
});

interface VwaPlateEntry {
  device: VwaDevice;
  plate: string;
  district: string | null;
  identifier: string | null;
}

@Component({
  name: "VwaDeviceList",
  components: {
    SingleBoardInformationModal
  },
  methods: {
    ...mapMutations("projects", {
      resetProject: "ResetProject"
    })
  }
})
export default class VwaDeviceList extends Vue {
  private resetProject!: Function;

  devices: Array<VwaDevice> = [];
  search = "";
  isLoading = false;
  loadingFailed = false;

  selectedSectionId: number | null = null;
  modalKey = 0;
  vwaProjectType = VWA_PROJECT_TYPE;
  starPoints = STAR_POINTS;
  euStars = EU_STARS;

  created() {
    // the settings components read the current project, make sure no other project is left in the store
    this.resetProject();
    this.loadDevices();
  }

  async loadDevices() {
    this.isLoading = true;
    try {
      const res = await VwaDevices.GetAllVwaDevices();
      this.devices = res.data.devices;
      this.loadingFailed = false;
    } catch (error) {
      this.loadingFailed = true;
    } finally {
      this.isLoading = false;
    }
  }

  plateOf(device: VwaDevice): string {
    return device.licensePlate || device.description || "";
  }

  get filteredDevices(): Array<VwaDevice> {
    return this.devices.filter((device) => matchesLicensePlate(this.plateOf(device), this.search));
  }

  get filteredPlates(): Array<VwaPlateEntry> {
    return this.filteredDevices.map((device) => {
      const plate = this.plateOf(device);
      const match = plate.trim().match(GERMAN_PLATE);
      return {
        device,
        plate,
        district: match ? match[1].toUpperCase() : null,
        identifier: match ? `${match[2]} ${match[3]}`.toUpperCase() : null
      };
    });
  }

  openDevice(device: VwaDevice) {
    this.selectedSectionId = device.sectionId;
    this.modalKey++;
    this.$nextTick(() => this.$bvModal.show("openDisplayModalDetails"));
  }

  handleModalClose() {
    // the modal reports both its close and its hide event
    if (this.selectedSectionId === null) {
      return;
    }
    this.selectedSectionId = null;
    this.loadDevices();
  }
}
